import {
  MolsonBox,
  MolsonCustomTooltip,
  MolsonTypography
} from '@components/reusable'
import {
  Accordion,
  AccordionDetails,
  type AccordionProps,
  AccordionSummary,
  type SxProps,
  type Theme,
  type TypographyPropsVariantOverrides
} from '@mui/material'
import type { Variant } from '@mui/material/styles/createTypography'
import type { OverridableStringUnion } from '@mui/types'
import type { FC, ReactNode } from 'react'
import { type IconColor, type IconVariant, MolsonIcon } from '../molson-icons'

export type MolsonAccordionProps = AccordionProps & {
  dataTestId?: string
  primaryText: string
  additionalSummaryComponent?: ReactNode
  primaryTextVariant?: OverridableStringUnion<
    Variant | 'inherit',
    TypographyPropsVariantOverrides
  >
  summarySx?: SxProps<Theme>
  summaryIcon?: IconVariant
  summaryIconColor?: IconColor
  withTooltip?: {
    title: string
    description: React.ReactNode
  }
  size?: 'sm' | 'md'
  primaryTextSx?: SxProps<Theme>
  detailsSx?: SxProps<Theme>
}

export const MolsonAccordion: FC<MolsonAccordionProps> = ({
  dataTestId,
  children,
  primaryText,
  additionalSummaryComponent,
  primaryTextVariant = 'h3',
  summarySx,
  withTooltip,
  size = 'md',
  summaryIcon,
  summaryIconColor,
  primaryTextSx,
  detailsSx,
  ...props
}) => {
  const summaryComponent = summaryIcon ? (
    <MolsonBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <MolsonIcon variant={summaryIcon} iconColor={summaryIconColor} />
      <MolsonTypography variant={primaryTextVariant} sx={primaryTextSx}>
        {primaryText}
      </MolsonTypography>
    </MolsonBox>
  ) : (
    <MolsonTypography variant={primaryTextVariant} sx={primaryTextSx}>
      {primaryText}
    </MolsonTypography>
  )

  const summaryWithoutAdditional = withTooltip ? (
    <MolsonBox alignItems="center" display="flex" gap={0.5}>
      {summaryComponent}
      <MolsonCustomTooltip
        config={{
          description: withTooltip.description,
          title: withTooltip.title
        }}
      />
    </MolsonBox>
  ) : (
    summaryComponent
  )

  return (
    <Accordion data-testid={dataTestId} {...props} disableGutters={true}>
      <AccordionSummary
        expandIcon={<MolsonIcon variant="CHEVRON_DOWN" />}
        sx={{
          padding: size === 'md' ? '20px 0px 30px' : '12px 0px',
          ...summarySx
        }}
      >
        {additionalSummaryComponent ? (
          <MolsonBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mr: 1,
              width: '100%'
            }}
          >
            {summaryComponent}
            {additionalSummaryComponent}
          </MolsonBox>
        ) : (
          summaryWithoutAdditional
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: size === 'md' ? '0px 0px 30px 0px' : '0px 0px 24px 0px',
          ...detailsSx
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  )
}
